import React, { useEffect, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { BulkImportDialog } from "../components/BulkImportDialog";
import { getLicenses, createLicense, updateLicense, deleteLicense, getMigrationSites, bulkCreateLicenses } from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  KeyRound, ChevronRight, Plus, Trash2, Save, X, AlertTriangle, CalendarClock, RefreshCw, FileUp,
} from "lucide-react";

// Days until a YYYY-MM-DD date (null if unparseable). Negative = past.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}

// Renewal urgency tone from a date: red (expired), amber (<90d), null otherwise.
function renewalTone(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return null;
  if (d < 0) return "expired";
  if (d <= 90) return "soon";
  return "ok";
}

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  expiring: { label: "Expiring", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  expired: { label: "Expired", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  needed: { label: "Needed", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
};
const CATEGORIES = ["CUCM", "Unity", "Expressway", "Contact Center", "Webex", "Licensing"];
const CAT_CLS = {
  CUCM: "text-blue-400", Unity: "text-purple-400", Expressway: "text-teal-400",
  "Contact Center": "text-amber-400", Webex: "text-emerald-400", Licensing: "text-zinc-400",
};

export default function Licenses() {
  const [licenses, setLicenses] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newLic, setNewLic] = useState({ name: "", category: "Webex", vendor: "Cisco", entitled: 0, assigned: 0, status: "needed", renewal_date: "" });

  const load = () => getLicenses().then((d) => { setLicenses(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); getMigrationSites().then(setSites).catch(() => {}); }, []);

  const cutoverUsers = sites
    .filter((s) => s.status === "cutover" || s.status === "onboarded")
    .reduce((a, s) => a + (s.users || 0), 0);

  const syncFromTracker = async (l) => {
    try {
      const updated = await updateLicense(l.id, { assigned: cutoverUsers });
      setLicenses((prev) => prev.map((x) => (x.id === l.id ? updated : x)));
      toast.success(`Synced ${cutoverUsers} assigned from onboarded/cutover sites`);
    } catch { toast.error("Sync failed"); }
  };

  const startEdit = (l) => { setEditId(l.id); setForm({ ...l }); };
  const saveEdit = async () => {
    try {
      const updated = await updateLicense(editId, {
        entitled: Number(form.entitled) || 0,
        assigned: Number(form.assigned) || 0,
        status: form.status,
        renewal_date: form.renewal_date || "",
        notes: form.notes || "",
      });
      setLicenses((prev) => prev.map((l) => (l.id === editId ? updated : l)));
      setEditId(null);
      toast.success("License updated");
    } catch { toast.error("Update failed"); }
  };

  const addLicense = async () => {
    if (!newLic.name.trim()) { toast.error("Enter a license name"); return; }
    try {
      const created = await createLicense({ ...newLic, entitled: Number(newLic.entitled) || 0, assigned: Number(newLic.assigned) || 0 });
      setLicenses((prev) => [...prev, created]);
      setAdding(false);
      setNewLic({ name: "", category: "Webex", vendor: "Cisco", entitled: 0, assigned: 0, status: "needed", renewal_date: "" });
      toast.success("License added");
    } catch { toast.error("Add failed"); }
  };

  const remove = async (id) => {
    try { await deleteLicense(id); setLicenses((prev) => prev.filter((l) => l.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const totalEntitled = licenses.reduce((a, l) => a + (l.entitled || 0), 0);
  const totalAssigned = licenses.reduce((a, l) => a + (l.assigned || 0), 0);
  const overAllocated = licenses.filter((l) => (l.assigned || 0) > (l.entitled || 0)).length;
  const expiringSoon = licenses.filter((l) => ["expired", "soon"].includes(renewalTone(l.renewal_date))).length;
  const attention = licenses.filter(
    (l) => ["expiring", "expired", "needed"].includes(l.status) || ["expired", "soon"].includes(renewalTone(l.renewal_date))
  ).length;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> licenses <ChevronRight className="h-3 w-3" /> tracker
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="no-print flex justify-end gap-2 mb-4">
          <button onClick={() => setShowImport(true)} data-testid="import-licenses-btn" className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
            <FileUp className="h-3.5 w-3.5" /> Import CSV
          </button>
          <PrintButton />
        </div>
        <BulkImportDialog
          open={showImport} onOpenChange={setShowImport} testid="import-licenses"
          title="Bulk import licenses"
          description="Upload a CSV or paste rows to add Cisco & Webex licenses in bulk."
          columns={[
            { key: "name", synonyms: ["license", "product"] },
            { key: "category", synonyms: ["cat", "type"] },
            { key: "vendor", synonyms: ["manufacturer"] },
            { key: "entitled", synonyms: ["licensed", "total", "qty", "quantity"] },
            { key: "assigned", synonyms: ["used", "consumed"] },
            { key: "status", synonyms: [] },
            { key: "renewal_date", synonyms: ["renewal", "renews", "renewal date", "expiry", "expires"] },
            { key: "notes", synonyms: ["note", "comment"] },
          ]}
          sampleCsv={"name,category,vendor,entitled,assigned,status,renewal_date,notes\nCUCM CUWL,CUCM,Cisco,500,420,active,2026-09-30,Enterprise agreement\nWebex Calling,Webex,Cisco,300,275,expiring,2026-03-15,"}
          sampleFilename="licenses_template.csv"
          onImport={bulkCreateLicenses}
          onDone={load}
        />
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">
          <KeyRound className="h-4 w-4" /> License Tracker
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Cisco &amp; Webex Licenses</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Track every license across the estate — Unified CM, Unity, Expressway, Contact Center and Webex —
          with entitled vs assigned counts, status, and renewal dates.
        </p>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
          <SummaryStat label="Entitled" value={totalEntitled} testId="stat-entitled" />
          <SummaryStat label="Assigned" value={totalAssigned} testId="stat-assigned" tone={totalAssigned > totalEntitled ? "red" : "default"} />
          <SummaryStat label="Over-allocated" value={overAllocated} testId="stat-over" tone={overAllocated > 0 ? "red" : "default"} />
          <SummaryStat label="Expiring ≤90d" value={expiringSoon} testId="stat-expiring" tone={expiringSoon > 0 ? "amber" : "default"} />
          <SummaryStat label="Need attention" value={attention} testId="stat-attention" tone={attention > 0 ? "amber" : "default"} />
        </div>

        {expiringSoon > 0 && (
          <div data-testid="renewal-alert-banner" className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <CalendarClock className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-[13px] text-amber-200">
              <span className="font-semibold">{expiringSoon} license{expiringSoon > 1 ? "s" : ""}</span> expiring within 90 days (or already expired). Plan renewals with your Smart Account team.
            </div>
          </div>
        )}

        {/* Table */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.9fr_1fr_auto] gap-3 px-4 py-2.5 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <span>License</span><span>Category</span><span>Entitled</span><span>Assigned</span><span>Status</span><span>Renewal</span><span></span>
          </div>

          {loading ? (
            <div className="p-6 text-zinc-500 font-mono text-sm">Loading licenses…</div>
          ) : (
            <div className="divide-y divide-zinc-800" data-testid="licenses-list">
              {licenses.map((l) => {
                const editing = editId === l.id;
                const over = (l.assigned || 0) > (l.entitled || 0);
                return (
                  <div key={l.id} data-testid={`license-row-${l.id}`} className="grid grid-cols-2 md:grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.9fr_1fr_auto] gap-3 px-4 py-3 items-center">
                    <div>
                      <div className="text-sm text-white font-medium">{l.name}</div>
                      <div className="text-[11px] text-zinc-500">{l.vendor}</div>
                    </div>
                    <div className={`text-[12px] font-semibold ${CAT_CLS[l.category] || "text-zinc-400"}`}>{l.category}</div>

                    {editing ? (
                      <>
                        <Input data-testid={`edit-entitled-${l.id}`} type="number" value={form.entitled} onChange={(e) => setForm({ ...form, entitled: e.target.value })} className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input data-testid={`edit-assigned-${l.id}`} type="number" value={form.assigned} onChange={(e) => setForm({ ...form, assigned: e.target.value })} className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                          <SelectTrigger data-testid={`edit-status-${l.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input data-testid={`edit-renewal-${l.id}`} value={form.renewal_date || ""} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} placeholder="YYYY-MM-DD" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <div className="flex items-center gap-1.5">
                          <button onClick={saveEdit} data-testid={`save-license-${l.id}`} className="h-8 w-8 flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Save className="h-4 w-4" /></button>
                          <button onClick={() => setEditId(null)} className="h-8 w-8 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300"><X className="h-4 w-4" /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`text-sm font-mono ${over ? "text-red-400" : "text-zinc-200"}`}>{l.entitled}</div>
                        <div className={`text-sm font-mono flex items-center gap-1 ${over ? "text-red-400" : "text-zinc-200"}`}>
                          {l.assigned}{over && <AlertTriangle className="h-3.5 w-3.5" />}
                        </div>
                        <div><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${STATUS[l.status]?.cls || ""}`}>{STATUS[l.status]?.label || l.status}</span></div>
                        {(() => {
                          const tone = renewalTone(l.renewal_date);
                          const days = daysUntil(l.renewal_date);
                          const cls = tone === "expired" ? "text-red-400" : tone === "soon" ? "text-amber-400" : "text-zinc-400";
                          return (
                            <div data-testid={`renewal-cell-${l.id}`} className={`text-[13px] font-mono flex items-center gap-1 ${cls}`}>
                              {(tone === "expired" || tone === "soon") && <CalendarClock className="h-3.5 w-3.5" />}
                              {l.renewal_date ? (
                                <span title={tone === "expired" ? `Expired ${Math.abs(days)}d ago` : tone === "soon" ? `${days}d left` : ""}>
                                  {l.renewal_date}
                                </span>
                              ) : "—"}
                            </div>
                          );
                        })()}
                        <div className="flex items-center gap-1.5">
                          {l.category === "Webex" && (
                            <button onClick={() => syncFromTracker(l)} data-testid={`sync-license-${l.id}`} title="Sync assigned from onboarded/cutover sites" className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800"><RefreshCw className="h-3.5 w-3.5" /></button>
                          )}
                          <button onClick={() => startEdit(l)} data-testid={`edit-license-${l.id}`} className="text-[11px] text-blue-400 hover:underline">Edit</button>
                          <button onClick={() => remove(l.id)} data-testid={`delete-license-${l.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add row */}
          {adding ? (
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/50" data-testid="add-license-form">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <Input data-testid="new-license-name" value={newLic.name} onChange={(e) => setNewLic({ ...newLic, name: e.target.value })} placeholder="License name" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newLic.category} onValueChange={(v) => setNewLic({ ...newLic, category: v })}>
                  <SelectTrigger data-testid="new-license-category" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" value={newLic.entitled} onChange={(e) => setNewLic({ ...newLic, entitled: e.target.value })} placeholder="Entitled" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input type="number" value={newLic.assigned} onChange={(e) => setNewLic({ ...newLic, assigned: e.target.value })} placeholder="Assigned" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newLic.status} onValueChange={(v) => setNewLic({ ...newLic, status: v })}>
                  <SelectTrigger className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
                <button onClick={addLicense} data-testid="confirm-add-license" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Add License</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="add-license-btn" className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-zinc-800 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/40 transition-colors">
              <Plus className="h-4 w-4" /> Add license
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const SummaryStat = ({ label, value, testId, tone = "default" }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-white";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
